import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
} from '@mui/material';
import { Close, Star, LocationOn, People } from '@mui/icons-material';

const UniversityComparison = ({ open, onClose, universities, onRemoveUniversity }) => {
  if (!universities || universities.length === 0) {
    return null;
  }
  const comparisonData = [
    {
      label: '基本信息',
      items: [
        { 
          key: 'name', 
          label: '大学名称',
          render: (uni) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                {uni.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                {uni.nameEn}
              </Typography>
            </Box>
          )
        },
        { 
          key: 'ranking', 
          label: 'QS世界排名',
          render: (uni) => {
            const ranking = uni.ranking || uni.rankingValue;
            const hasRanking = ranking && ranking !== '-' && ranking !== 'null' && ranking !== 'N/A';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Star sx={{ color: 'warning.main', mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {hasRanking ? `第 ${ranking} 名` : '-'}
                </Typography>
              </Box>
            );
          }
        },
        { 
          key: 'location', 
          label: '地理位置',
          render: (uni) => {
            // 处理 location 可能是对象或字符串的情况
            let locationText = '';
            if (typeof uni.location === 'string') {
              locationText = uni.location;
            } else if (uni.location && typeof uni.location === 'object') {
              locationText = [uni.location.state, uni.location.city].filter(Boolean).join(', ');
            }
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LocationOn sx={{ color: 'text.secondary', mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2">
                  {locationText || '-'}
                </Typography>
              </Box>
            );
          }
        },
        { 
          key: 'type', 
          label: '大学类型',
          render: (uni) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Chip 
              label={uni.type} 
              size="small" 
              variant="outlined"
              color="primary"
            />
            </Box>
          )
        },
        { 
          key: 'established', 
          label: '成立年份',
          render: (uni) => {
            const established = uni.established;
            const hasEstablished = established && established !== '-' && established !== 'null' && established !== 'N/A';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2">
                  {hasEstablished ? `${established} 年` : '-'}
                </Typography>
              </Box>
            );
          }
        },
      ]
    },
    {
      label: '学生信息',
      items: [
        { 
          key: 'totalStudents', 
          label: '在校学生总数',
          render: (uni) => {
            const total = uni.students?.total;
            const displayTotal = typeof total === 'number' && total > 0 ? total.toLocaleString() : '-';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <People sx={{ color: 'text.secondary', mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2">
                  {displayTotal} 人
                </Typography>
              </Box>
            );
          }
        },
        { 
          key: 'internationalStudents', 
          label: '国际学生',
          render: (uni) => {
            const total = typeof uni.students?.total === 'number' ? uni.students.total : 0;
            const international = typeof uni.students?.international === 'number' ? uni.students.international : 0;
            const percentage = total > 0 ? ((international / total) * 100).toFixed(1) : '0.0';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2">
                  {international > 0 ? international.toLocaleString() : '-'} 人
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({percentage}%)
                  </Typography>
                </Typography>
              </Box>
            );
          }
        },
        { 
          key: 'undergraduate', 
          label: '本科生',
          render: (uni) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>  
            <Typography variant="body2">
              {uni.students?.undergraduate ? uni.students.undergraduate.toLocaleString() : '-'} 人
            </Typography>
            </Box>
          )
        },
        { 
          key: 'postgraduate', 
          label: '研究生',
          render: (uni) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">
              {uni.students?.postgraduate ? uni.students.postgraduate.toLocaleString() : '-'} 人
            </Typography>
            </Box>
          )
        },
      ]
    },
    {
      label: '费用信息',
      items: [
        { 
          key: 'undergraduateTuition', 
          label: '本科学费范围',
          render: (uni) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">
              RM {uni.tuition?.undergraduate?.min ? uni.tuition.undergraduate.min.toLocaleString() : '-'} - {uni.tuition?.undergraduate?.max ? uni.tuition.undergraduate.max.toLocaleString() : '-'} /年
            </Typography>
            </Box>
          )
        },
        { 
          key: 'postgraduateTuition', 
          label: '研究生学费范围',
          render: (uni) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">
              RM {uni.tuition?.postgraduate?.min ? uni.tuition.postgraduate.min.toLocaleString() : '-'} - {uni.tuition?.postgraduate?.max ? uni.tuition.postgraduate.max.toLocaleString() : '-'} /年
            </Typography>
            </Box>
          )
        },
      ]
    },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            大学对比 ({universities.length}/3)
          </Typography>
          <IconButton 
            onClick={onClose} 
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>
                  对比项目
                </TableCell>
                {universities.map((university) => (
                  <TableCell key={university.id} sx={{ textAlign: 'center', minWidth: 200 }}>
                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Avatar
                        src={university.logo}
                        alt={university.name}
                        sx={{ 
                          width: 60, 
                          height: 60, 
                          mx: 'auto', 
                          mb: 1,
                          border: '3px solid',
                          borderColor: 'primary.main'
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => onRemoveUniversity(university.id)}
                        sx={{
                          position: 'absolute',
                          top: -5,
                          right: '50%',
                          transform: 'translateX(50%)',
                          backgroundColor: 'error.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'error.dark' },
                          width: 20,
                          height: 20,
                        }}
                      >
                        <Close sx={{ fontSize: 12 }} />
                      </IconButton>
                                              <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                          {university.name}
                        </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonData.map((section) => (
                <React.Fragment key={section.label}>
                  <TableRow>
                    <TableCell
                      colSpan={universities.length + 1}
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        py: 1,
                      }}
                    >
                      {section.label}
                    </TableCell>
                  </TableRow>
                  {section.items.map((item) => (
                    <TableRow key={item.key} hover>
                      <TableCell sx={{ fontWeight: 500, backgroundColor: 'grey.50' }}>
                        {item.label}
                      </TableCell>
                      {universities.map((university) => (
                        <TableCell key={university.id} sx={{ textAlign: 'center' }}>
                          {item.render(university)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: 'grey.50' }}>
        <Button onClick={onClose} variant="outlined">
          关闭对比
        </Button>
        <Button 
          variant="contained" 
          onClick={() => {
            // 可以在这里实现导出对比结果的功能
            console.log('导出对比结果');
          }}
        >
          导出对比
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UniversityComparison;
